/**
 * GraphQL utility functions for optimized product fetching
 * Provides consolidated queries to reduce API calls and improve performance
 */

/**
 * Consolidated GraphQL query to fetch all product details with pagination
 * @param {string[]} styleNumbers - Array of style numbers to fetch
 * @param {string} shopUrl - Shopify shop URL
 * @param {AbortController} abortController - Abort controller for request cancellation
 * @returns {Promise<Object>} Products grouped by style number
 */
const fetchAllProductDetails = async (styleNumbers, shopUrl, abortController) => {
    const storefrontAccessToken = 'e37d6f5fced72df1026762e8fcf43b21';
    const url = `https://${shopUrl}/api/2024-10/graphql.json`;
    
    if (!styleNumbers || styleNumbers.length === 0) {
        console.warn('No style numbers provided for consolidated fetch');
        return {};
    }
        
    // Create a query that searches for multiple style numbers
    const queryConditions = styleNumbers.map(styleNo => `tag:${styleNo}`).join(' OR ') + ' AND status:ACTIVE';
    
    const productQuery = `
        query Products($query: String!, $country: CountryCode, $first: Int!, $after: String) @inContext(country: $country) {
            products(first: $first, after: $after, query: $query) {
                pageInfo {
                    hasNextPage
                    endCursor
                }
                edges {
                    node {
                        id
                        title
                        handle
                        tags
                        discontinued: metafield(namespace: "custom", key: "discontinued") {
                            value
                            type
                        }
                        merchReportingStyleNumber: metafield(namespace: "custom", key: "merch_reporting_style_number") {
                            value
                            type
                        }
                        variantSwatchColor: metafield(namespace: "custom", key: "variant_swatch_color") {
                            value
                            type
                        }
                        totalInventory
                        priceRange {
                            maxVariantPrice {
                                amount
                                currencyCode
                            }
                            minVariantPrice {
                                amount
                                currencyCode
                            }
                        }
                        images(first: 2) {
                            edges {
                                node {
                                    src
                                    altText
                                }
                            }
                        }
                        variants(first: 100) {
                            edges {
                                node {
                                    id
                                    title
                                    quantityAvailable
                                    compareAtPrice {
                                        amount
                                        currencyCode
                                    }
                                    selectedOptions {
                                        name
                                        value
                                    }
                                    image {
                                        src
                                        altText
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    `;

    try {
        let allProducts = [];
        let hasNextPage = true;
        let endCursor = null;
        let pageCount = 0;
        const maxPages = 10; // Safety limit to prevent infinite loops

        // Use while loop to fetch all pages
        while (hasNextPage && pageCount < maxPages) {
            if (abortController?.signal?.aborted) {
                console.log('Request aborted during pagination');
                break;
            }            
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    query: productQuery,
                    variables: {
                        query: queryConditions,
                        country: Shopify.country,
                        first: 250,
                        after: endCursor
                    }
                }),
                signal: abortController?.signal
            });

            const { data, errors } = await response.json();

            if (errors) {
                console.error('GraphQL Errors:', errors);
                break;
            }

            if (data && data?.products?.edges) {
                const pageProducts = data.products.edges
                    .map(({ node }) => {
                        if (node) {
                            return node;
                        } else {
                            console.warn("Encountered a null product.");
                            return null;
                        }
                    })
                    .filter(Boolean);

                allProducts = allProducts.concat(pageProducts);
                
                // Update pagination info
                hasNextPage = data.products.pageInfo.hasNextPage;
                endCursor = data.products.pageInfo.endCursor;
                pageCount++;
            } else {
                console.warn("No product data found on page", pageCount + 1);
                break;
            }
        }

        if (pageCount >= maxPages) {
            console.warn(`Reached maximum page limit (${maxPages}). There may be more products available.`);
        }

        // Group products by their style number for easy lookup
        const productsByStyle = {};
        allProducts.forEach(product => {
            const styleNumber = product.merchReportingStyleNumber?.value;
            if (styleNumber) {
                if (!productsByStyle[styleNumber]) {
                    productsByStyle[styleNumber] = [];
                }
                productsByStyle[styleNumber].push(product);
            }
        });
        return productsByStyle;

    } catch (error) {
        console.error("Error fetching consolidated product details:", error);
        return {};
    }
};

/**
 * Process custom swatches with consolidated data fetching and pagination
 * @param {Function} processProductDetails - Function to process individual product details
 * @param {AbortController} abortController - Abort controller for request cancellation
 * @param {string} shopUrl - Shopify shop URL
 * @returns {Promise<void>}
 */
const processCustomSwatchesOptimized = async (processProductDetails, abortController, shopUrl) => {
    sessionStorage.removeItem('natoriCustomVariants');
    const productCards = document.querySelectorAll(".product-card-swatch");

    // Collect all style numbers first
    const styleNumbers = [];
    const cardData = [];
    
    for (const card of productCards) {
        const productId = card.getAttribute('data-product-id');
        const productStyleNo = card.getAttribute('data-style-no');
        const productName = card.getAttribute('data-product-name');
        
        if (productStyleNo && !styleNumbers.includes(productStyleNo)) {
            styleNumbers.push(productStyleNo);
        }
        
        cardData.push({
            card,
            productId,
            productStyleNo,
            productName
        });
    }
    
    // Fetch all product details with pagination support
    let allProductDetails = {};
    if (styleNumbers.length > 0) {
        try {
            console.log(`Starting consolidated fetch for ${styleNumbers.length} style numbers with pagination support`);
            allProductDetails = await fetchAllProductDetails(styleNumbers, shopUrl, abortController);
        } catch (error) {
            console.error('Error fetching consolidated product details:', error);
            return;
        }
    }

    // Process each product card with the consolidated data
    for (const { card, productId, productStyleNo, productName } of cardData) {
        const prodOldSwatches = card.querySelector('.product-card-swatches');
        if (prodOldSwatches) {
            prodOldSwatches.innerHTML = '';
        }

        const color_settings = window['settings'];
        const settings = window[`settings_${productId}`];

        const customColorsArray = color_settings.color_swatches.split(',').map(color => {
            const [key, value] = color.split(':').map(part => part.trim());
            return { key: key.toLowerCase(), value: value || '' };
        });

        const keyArray = customColorsArray.map(color => color.key);
        const valArray = customColorsArray.map(color => color.value);

        // Get product details from consolidated data
        const productDetails = allProductDetails[productStyleNo] || [];

        let uniqueColors = new Map();
        let counter = { count: 0 };

        const secondaryImageSrc = productDetails.filter(pd => pd.id === `gid://shopify/Product/${productId}`).flatMap(pd => pd.images.edges).map(edge => edge.node.src)[1];
        const prodCard = document.querySelector(`product-card.product-card:has(.product-card-swatch[data-product-id='${productId}'])`);
        const prodImage = prodCard.querySelector('figure.product-featured-image img.product-primary-image');
        const featuredImage = prodCard.querySelector('figure.product-featured-image');
        const prodImageContainer = prodCard.querySelector('figure.product-featured-image .product-featured-image-link');
        
        if(secondaryImageSrc){
            const prodSecondaryImage = document.createElement('img');
            prodSecondaryImage.src = secondaryImageSrc;
            prodSecondaryImage.alt = prodImage.alt ?? productName;
            prodSecondaryImage.style.width = '100%';
            prodSecondaryImage.style.height = '100%';
            prodSecondaryImage.classList.add('product-secondary-image');
            prodImageContainer.insertBefore(prodSecondaryImage, prodImage);
            featuredImage.classList.add('thb-hover');
        }

        const minPrice = Math.min(...productDetails.filter(pd => pd.totalInventory > 0).map(pd => pd.priceRange.minVariantPrice.amount));
        const maxPrice = Math.max(...productDetails.filter(pd => pd.totalInventory > 0).map(pd => pd.priceRange.maxVariantPrice.amount));
        const compareAtPrice = Math.min(...productDetails.filter(pd => pd.totalInventory > 0).flatMap(pd => pd.variants.edges.map(edge => edge.node?.compareAtPrice?.amount).filter(amount => amount != null))) || undefined;

        const prodPrices = prodCard.querySelector('.product-card-info .productInfoRight span.price');
        const priceContainer = minPrice !== maxPrice
            ? `<ins><span class="amount">${moneyFormat}${formatPrice(minPrice)} - ${moneyFormat}${formatPrice(maxPrice)}</span></ins>`
            : `<ins><span class="amount">${moneyFormat}${formatPrice(maxPrice)}</span></ins>`;
        const comparePriceContainer = compareAtPrice > minPrice ? `<del><span class="amount">${moneyFormat}${formatPrice(compareAtPrice)}</span></del>` : '';
        if (isFinite(minPrice) && isFinite(maxPrice) && (compareAtPrice === undefined || isFinite(compareAtPrice))) {
            prodPrices.innerHTML = comparePriceContainer + priceContainer;
        }
        prodCard.classList.remove('hide-price');

        for (const productDetail of productDetails) {
            if (abortController.signal.aborted) return;

            const productAvailable = productDetail.totalInventory > 0;
            const discontinued = productDetail?.discontinued?.value === "true";

            if (productAvailable && !discontinued) {
                const images = productDetail.images.edges.map(edge => edge.node);
                const primaryImagesrc = images[0]?.src || '';
                const secondaryImagesrc = images[1]?.src || '';

                await processProductDetails(
                    productDetail,
                    keyArray,
                    valArray,
                    settings.featured_image_id,
                    document.getElementById("swatch_section_" + productId),
                    productId,
                    primaryImagesrc,
                    secondaryImagesrc,
                    settings.custom_handle,
                    uniqueColors,
                    counter,
                    productAvailable
                );
                if (counter.count >= 6) break;
            }
        }

        const swatchSection = document.getElementById("swatch_section_" + productId);
        if (swatchSection) {
            swatchSection.parentNode.parentNode.parentNode.enableSwatches();
        }
    }

    try {
        await handleProductCardHover();
        await collectionSwatchImages();
    } catch (error) {
        console.error('Error handling swatch images or hover events:', error);
    }
};

// Export functions for use in other files
if (typeof module !== 'undefined' && module.exports) {
    // Node.js environment
    module.exports = { fetchAllProductDetails, processCustomSwatchesOptimized };
} else {
    // Browser environment
    window.GraphQLUtils = { fetchAllProductDetails, processCustomSwatchesOptimized };
}
